import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { notify, type MarSidebarReorder, type MarSidebarSplitCollapsed } from '@nimling/components-nimtech'
import type { MobileTab, Page, SidebarGroup } from '@/types'
import { useSettingsStore } from '@/stores/settings'
import { usePagesStore } from '@/stores/pages'

const STORAGE_KEY = 'hvergel-ui'

export const MOBILE_TAB_OPTIONS: MobileTab[] = [
  { key: 'pages', label: 'Pages', icon: 'dashboard', href: '/pages' },
  { key: 'logs', label: 'Logs', icon: 'list', href: '/streams' },
  { key: 'alerts', label: 'Alerts', icon: 'triangle-alert', href: '/alerts' },
  { key: 'notifications', label: 'Notifications', icon: 'notification', href: '/notifications' },
  { key: 'views', label: 'Views', icon: 'eye', href: '/views' },
  { key: 'sources', label: 'Sources', icon: 'database', href: '/sources' },
  { key: 'annotations', label: 'Annotations', icon: 'flag', href: '/annotations' },
  { key: 'telemetry', label: 'Telemetry', icon: 'activity', href: '/telemetry' },
  { key: 'search', label: 'Search', icon: 'search' },
]

const MOBILE_TABS_DEFAULT = ['pages', 'logs', 'notifications']

interface StoredUi {
  sidebarCollapsed?: boolean
  sidebarSplit?: number
  sidebarSplitCollapsed?: MarSidebarSplitCollapsed
  mobileTabs?: string[]
}

function getStoredUi(): StoredUi {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as StoredUi) : {}
  } catch {
    return {}
  }
}

export const useUiStore = defineStore('ui', () => {
  const settingsStore = useSettingsStore()
  const pagesStore = usePagesStore()
  const stored = ref(getStoredUi())
  const sidebarCollapsed = ref(!!stored.value.sidebarCollapsed)

  const sidebarOrder = computed<string[]>(() => settingsStore.me.sidebar?.order ?? [])
  const sidebarGroups = computed<SidebarGroup[]>(() => settingsStore.me.sidebar?.groups ?? [])
  const sidebarSplit = computed(() => settingsStore.me.sidebar?.split ?? stored.value.sidebarSplit)
  const sidebarSplitCollapsed = computed<MarSidebarSplitCollapsed>(() => settingsStore.me.sidebar?.split_collapsed ?? stored.value.sidebarSplitCollapsed ?? {})
  const mobileTabs = computed<string[]>(() => settingsStore.me.mobile_tabs ?? stored.value.mobileTabs ?? MOBILE_TABS_DEFAULT)

  const pagesById = computed(() => new Map(pagesStore.pages.map((page) => [page.id, page])))

  const orderIndex = computed(() => new Map(sidebarOrder.value.map((id, i) => [id, i])))

  const byOrder = (a: Page, b: Page) => (orderIndex.value.get(a.id) ?? Infinity) - (orderIndex.value.get(b.id) ?? Infinity)

  const getParentId = (page: Page): string | null => (page.parent_id && pagesById.value.has(page.parent_id) ? page.parent_id : null)

  const sidebarPageTree = computed(() => {
    const tree = new Map<string, Page[]>()
    for (const page of pagesStore.pages) {
      const parentId = getParentId(page)
      if (parentId) tree.set(parentId, [...(tree.get(parentId) ?? []), page])
    }
    for (const children of tree.values()) children.sort(byOrder)
    return tree
  })

  const sidebarSections = computed(() =>
    sidebarGroups.value.map((group) => ({
      ...group,
      pages: group.items.map((id) => pagesById.value.get(id)).filter((page): page is Page => !!page && !getParentId(page)),
    })),
  )

  const sidebarPages = computed(() => {
    const grouped = new Set(sidebarGroups.value.flatMap((group) => group.items))
    return pagesStore.pages.filter((page) => !getParentId(page) && !grouped.has(page.id)).sort(byOrder)
  })

  function putStoredUi(patch: StoredUi) {
    stored.value = { ...stored.value, ...patch }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored.value))
  }

  watch([sidebarSplit, sidebarSplitCollapsed], ([split, collapsed]) => putStoredUi({ sidebarSplit: split, sidebarSplitCollapsed: collapsed }))
  watch(mobileTabs, (tabs) => putStoredUi({ mobileTabs: tabs }))

  function setSidebarCollapsed(collapsed: boolean) {
    sidebarCollapsed.value = collapsed
    putStoredUi({ sidebarCollapsed: collapsed })
  }

  function toggleSidebar() {
    setSidebarCollapsed(!sidebarCollapsed.value)
  }

  function putMobileTab(slot: number, key: string) {
    settingsStore.putMeSettings({ mobile_tabs: mobileTabs.value.map((at, i) => (i === slot ? key : at)) })
  }

  function putSidebarOrder(next: string[]) {
    settingsStore.putMeSettings({ sidebar: { ...settingsStore.me.sidebar, order: next } })
  }

  function putSidebarGroups(next: SidebarGroup[]) {
    settingsStore.putMeSettings({ sidebar: { ...settingsStore.me.sidebar, groups: next } })
  }

  function putSidebarSplit(next: number | undefined) {
    settingsStore.putMeSettings({ sidebar: { ...settingsStore.me.sidebar, split: next } })
  }

  function putSidebarSplitCollapsed(next: MarSidebarSplitCollapsed) {
    settingsStore.putMeSettings({ sidebar: { ...settingsStore.me.sidebar, split_collapsed: next } })
  }

  function postSidebarGroup(label: string, icon: string): string {
    const id = crypto.randomUUID()
    putSidebarGroups([...sidebarGroups.value, { id, label, icon, items: [] }])
    return id
  }

  function putSidebarGroup(id: string, label: string, icon: string) {
    putSidebarGroups(sidebarGroups.value.map((group) => (group.id === id ? { ...group, label, icon } : group)))
  }

  function putSidebarPageGroup(pageId: string, groupId: string | null) {
    putSidebarGroups(sidebarGroups.value.map((group) => {
      const items = group.items.filter((id) => id !== pageId)
      return { ...group, items: group.id === groupId ? [...items, pageId] : items }
    }))
  }

  function deleteSidebarGroup(id: string) {
    putSidebarGroups(sidebarGroups.value.filter((group) => group.id !== id))
  }

  const holdsPage = (pageId: string, ancestorId: string): boolean => {
    for (let at = pagesById.value.get(pageId); at; at = at.parent_id ? pagesById.value.get(at.parent_id) : undefined) {
      if (at.id === ancestorId) return true
    }
    return false
  }

  const putPageParent = async (page: Page, parentId: string | null): Promise<boolean> => {
    if ((page.parent_id ?? null) === parentId) return true
    try {
      await pagesStore.putPage({ ...page, parent_id: parentId })
      return true
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Moving the page failed')
      return false
    }
  }

  async function putSidebarReorder(move: MarSidebarReorder) {
    const groups = sidebarGroups.value.map((group) => ({ ...group, items: [...group.items] }))
    if (groups.some((group) => group.id === move.draggedId)) {
      const from = groups.findIndex((group) => group.id === move.draggedId)
      const [moved] = groups.splice(from, 1)
      const to = groups.findIndex((group) => group.id === move.targetId)
      if (to < 0 || !moved) return
      groups.splice(move.position === 'before' ? to : to + 1, 0, moved)
      putSidebarGroups(groups)
      return
    }
    const dragged = pagesById.value.get(move.draggedId)
    if (!dragged) return
    const target = pagesById.value.get(move.targetId)
    const parentId = target ? (move.position === 'inside' ? target.id : getParentId(target)) : null
    if (parentId && holdsPage(parentId, dragged.id)) return
    if (!(await putPageParent(dragged, parentId))) return
    const fromGroup = groups.find((group) => group.items.includes(move.draggedId))
    if (fromGroup) fromGroup.items = fromGroup.items.filter((id) => id !== move.draggedId)
    const order = [...pagesStore.pages].sort(byOrder).map((page) => page.id).filter((id) => id !== move.draggedId)
    const targetGroup = groups.find((group) => group.id === move.targetId)
    const memberGroup = parentId ? undefined : groups.find((group) => group.items.includes(move.targetId))
    if (targetGroup) targetGroup.items.push(move.draggedId)
    else if (memberGroup) {
      const to = memberGroup.items.indexOf(move.targetId)
      memberGroup.items.splice(move.position === 'after' ? to + 1 : to, 0, move.draggedId)
    }
    const to = order.indexOf(move.targetId)
    if (to < 0 || move.position === 'inside') order.push(move.draggedId)
    else order.splice(move.position === 'after' ? to + 1 : to, 0, move.draggedId)
    putSidebarGroups(groups)
    putSidebarOrder(order)
  }

  return {
    sidebarCollapsed, mobileTabs, sidebarOrder, sidebarGroups, sidebarSplit, sidebarSplitCollapsed, sidebarPageTree, sidebarSections, sidebarPages,
    setSidebarCollapsed, toggleSidebar, putMobileTab, putSidebarOrder, putSidebarGroups, putSidebarSplit, putSidebarSplitCollapsed, postSidebarGroup, putSidebarGroup, putSidebarPageGroup, deleteSidebarGroup, putSidebarReorder,
  }
})
